import { Empty, Grid, Pagination, Selection, Toolbar } from "@/components/collection"
import { PageContent } from "@/components/layout/dashboard"
import globalConfig from "@/config/global"
import { useFilteredWorkshops } from "@/store/workshops/hooks"
import { ArchiveIcon, Eye, EyeOff, FilterX, PackageOpen } from "lucide-react"
import { usePageContext } from "./Context"
import { Item } from "./Item"

/**
 * dictionary src/dictionaries/en/pages/dashboard/kultureralley/workshops.json
 */
const dictionary = createContextMapper("pages", "dashboard", "kultureralley", "workshops")

/**
 * Collection
 */
export const Collection: React.FC = () => {
  const { _ } = useDictionary(dictionary())
  const { place, view, selected, setSelected, ...ctx } = usePageContext()
  const { workshops, filtered, matchable, sortable, filterable, paginated, paginable } =
    useFilteredWorkshops(place.id)
  return (
    <PageContent>
      <Selection
        selected={selected}
        deleteSelection={ctx.confirmDeleteSelection}
        publishSelection={ctx.confirmPublishSelection}
        unpublishSelection={ctx.confirmUnpublishSelection}
        unselect={() => setSelected([])}
      />
      <Toolbar>
        <Toolbar.Search {...matchable} />
        <Toolbar.Button onClick={ctx.create}>{_("create")}</Toolbar.Button>
        <Filters {...filterable} />
        <Toolbar.Sort {...sortable} dictionary={dictionary("sort")} />
      </Toolbar>
      <Empty
        items={workshops}
        results={filtered}
        create={ctx.create}
        clear={() => {
          filterable.clear()
          matchable.setSearch("")
          setSelected([])
        }}
      />
      <Grid view={view}>
        {A.map(paginated, item => (
          <Item key={item.id} workshop={item} />
        ))}
      </Grid>
      <Pagination {...paginable} />
    </PageContent>
  )
}

/**
 * Filters
 */
const Filters: React.FC<ReturnType<typeof useFilteredWorkshops>["filterable"]> = ({
  isActive,
  toggleActive,
  isInactive,
  toggleInactive,
  toggle,
  reset,
}) => {
  const { _ } = useDictionary(dictionary("filters"))
  const { setSelected } = usePageContext()

  return (
    <Toolbar.Filters onClick={() => setSelected([])}>
      {!globalConfig.disableKulturrallyeArchive && (
        <>
          <Toolbar.Filters.Item onClick={() => toggle("archived")} active={isInactive("archived")}>
            <PackageOpen aria-hidden />
            {_("unarchived")}
          </Toolbar.Filters.Item>
          <Toolbar.Filters.Item onClick={() => toggle("archived")} active={isActive("archived")}>
            <ArchiveIcon aria-hidden />
            {_("archived")}
          </Toolbar.Filters.Item>
        </>
      )}

      <Toolbar.Filters.Item
        onClick={() => toggleActive("published")}
        active={isActive("published")}
      >
        <Eye aria-hidden />
        {_("published")}
      </Toolbar.Filters.Item>
      <Toolbar.Filters.Item
        onClick={() => toggleInactive("published")}
        active={isInactive("published")}
      >
        <EyeOff aria-hidden />
        {_("unpublished")}
      </Toolbar.Filters.Item>

      <Toolbar.Filters.Separator />
      <Toolbar.Filters.Item onClick={reset}>
        <FilterX aria-hidden />
        {_("reset")}
      </Toolbar.Filters.Item>
    </Toolbar.Filters>
  )
}
